/** One registry-typed value editor (bool / enum / int / template / languages / catalogue
 *  product), generated from the setting's registry entry — the settings drawer, the bulk "set a
 *  setting" dialog and the tenant-defaults form all render values through it, so a new key needs
 *  no console code. An unknown `type` falls back to a JSON text field rather than disappearing. */
import {
  Autocomplete,
  Box,
  Chip,
  FormControlLabel,
  ListItemText,
  MenuItem,
  Switch,
  TextField,
  Tooltip,
} from '@mui/material';
import { useEffect, useState } from 'react';

import type { CatalogueOption, SettingSource, SettingSpec } from '@/api/catalogue';

import { CatalogueProductControl, type ProductPickerContext } from './CatalogueProductControl';
import { choicesFor, SOURCE_HINTS, sourceLabel } from './catalogueModel';

const MONO = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';

export interface SettingControlProps {
  spec: SettingSpec;
  value: unknown;
  onChange: (value: unknown) => void;
  /** The product's options: an enum value invalid for THIS product is shown disabled, with
   *  the reason (the server refuses it anyway). */
  options?: readonly CatalogueOption[];
  /** Suggestions for a `languages` value (the vendor's languages for the product). */
  languageSuggestions?: readonly string[];
  /** Where a `catalogue_product` value is picked from (the catalogue, minus the product itself). */
  products?: ProductPickerContext;
  disabled?: boolean;
  error?: string | null;
  /** Visible label (default: none — the row label names the control). */
  label?: string;
  /** The accessible name when there is no visible label (default: the registry label). */
  ariaLabel?: string;
  fullWidth?: boolean;
}

export function SettingControl({
  spec,
  value,
  onChange,
  options,
  languageSuggestions = [],
  products,
  disabled = false,
  error,
  label,
  ariaLabel,
  fullWidth = true,
}: SettingControlProps) {
  const name = ariaLabel ?? spec.label;
  const helper = error || undefined;

  if (spec.type === 'bool') {
    return (
      <Box>
        <Switch
          size="small"
          checked={value === true}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
          inputProps={{ 'aria-label': name }}
        />
        {helper && (
          <Box component="span" sx={{ color: 'error.main', typography: 'caption', ml: 1 }}>
            {helper}
          </Box>
        )}
      </Box>
    );
  }

  if (spec.type === 'enum') {
    const choices = choicesFor(spec, options);
    const current = typeof value === 'string' ? value : '';
    return (
      <TextField
        select
        size="small"
        fullWidth={fullWidth}
        label={label}
        value={choices.some((c) => c.value === current) ? current : ''}
        disabled={disabled}
        error={Boolean(error)}
        helperText={helper}
        onChange={(e) => onChange(e.target.value)}
        inputProps={{ 'aria-label': name }}
        SelectProps={{ renderValue: (v) => choices.find((c) => c.value === v)?.label ?? String(v) }}
      >
        {choices.map((c) => (
          <MenuItem key={c.value} value={c.value} disabled={c.blocked !== null}>
            <ListItemText
              primary={c.label}
              secondary={c.blocked ?? (c.label !== c.value ? c.value : null)}
              secondaryTypographyProps={{ variant: 'caption', sx: { whiteSpace: 'normal' } }}
            />
          </MenuItem>
        ))}
      </TextField>
    );
  }

  if (spec.type === 'int') {
    return (
      <TextField
        size="small"
        type="number"
        fullWidth={fullWidth}
        label={label}
        value={value === null || value === undefined ? '' : String(value)}
        disabled={disabled}
        error={Boolean(error)}
        helperText={helper}
        onChange={(e) => {
          const raw = e.target.value;
          const n = Number(raw);
          // A non-integer is sent as typed: the registry refuses it with the reason.
          onChange(raw !== '' && Number.isInteger(n) ? n : raw);
        }}
        inputProps={{
          'aria-label': name,
          ...(spec.minimum != null ? { min: spec.minimum } : {}),
          ...(spec.maximum != null ? { max: spec.maximum } : {}),
        }}
        sx={{ maxWidth: fullWidth ? undefined : 140 }}
      />
    );
  }

  if (spec.type === 'template') {
    return (
      <TextField
        size="small"
        fullWidth={fullWidth}
        label={label}
        value={typeof value === 'string' ? value : ''}
        disabled={disabled}
        error={Boolean(error)}
        helperText={helper}
        placeholder="{product.title} — {option.title}"
        onChange={(e) => onChange(e.target.value)}
        inputProps={{ 'aria-label': name, spellCheck: false }}
        InputProps={{ sx: { fontFamily: MONO, fontSize: 13 } }}
      />
    );
  }

  if (spec.type === 'languages') {
    return (
      <LanguagesControl
        name={name}
        value={value}
        onChange={onChange}
        suggestions={languageSuggestions}
        disabled={disabled}
        error={error}
      />
    );
  }

  if (spec.type === 'catalogue_product') {
    return (
      <CatalogueProductControl
        name={name}
        value={value}
        onChange={onChange}
        picker={products}
        disabled={disabled}
        error={error}
        label={label}
      />
    );
  }

  return (
    <JsonControl
      name={name}
      value={value}
      onChange={onChange}
      disabled={disabled}
      error={error}
      fullWidth={fullWidth}
    />
  );
}

/** `auto` (every language the vendor offers) or an explicit list of language codes. */
function LanguagesControl({
  name,
  value,
  onChange,
  suggestions,
  disabled,
  error,
}: {
  name: string;
  value: unknown;
  onChange: (v: unknown) => void;
  suggestions: readonly string[];
  disabled: boolean;
  error?: string | null;
}) {
  const auto = value === 'auto' || value === null || value === undefined;
  const list = Array.isArray(value) ? (value as string[]) : [];
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flexWrap: 'wrap' }}>
      <FormControlLabel
        sx={{ mr: 0 }}
        control={
          <Switch
            size="small"
            checked={auto}
            disabled={disabled}
            onChange={(e) => onChange(e.target.checked ? 'auto' : suggestions.length ? [...suggestions] : [])}
            inputProps={{ 'aria-label': `${name}: auto` }}
          />
        }
        label="Auto"
      />
      {!auto && (
        <Autocomplete
          multiple
          freeSolo
          size="small"
          options={[...suggestions]}
          value={list}
          disabled={disabled}
          onChange={(_, v) => onChange(v.map((x) => String(x).trim()).filter(Boolean))}
          renderTags={(vals, getTagProps) =>
            vals.map((v, i) => {
              const { key, ...tagProps } = getTagProps({ index: i });
              return <Chip key={key} size="small" label={v} {...tagProps} />;
            })
          }
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="en, fr, pt-BR…"
              error={Boolean(error)}
              helperText={error || undefined}
              inputProps={{ ...params.inputProps, 'aria-label': name }}
            />
          )}
          sx={{ flex: 1, minWidth: 220 }}
        />
      )}
    </Box>
  );
}

/** Fallback for a registry type this console does not know yet: edit the JSON value. */
function JsonControl({
  name,
  value,
  onChange,
  disabled,
  error,
  fullWidth,
}: {
  name: string;
  value: unknown;
  onChange: (v: unknown) => void;
  disabled: boolean;
  error?: string | null;
  fullWidth: boolean;
}) {
  const [text, setText] = useState(() => JSON.stringify(value ?? null));
  const [bad, setBad] = useState(false);
  useEffect(() => {
    setText(JSON.stringify(value ?? null));
  }, [value]);
  return (
    <TextField
      size="small"
      fullWidth={fullWidth}
      value={text}
      disabled={disabled}
      error={bad || Boolean(error)}
      helperText={bad ? 'Not valid JSON' : error || 'JSON value'}
      onChange={(e) => setText(e.target.value)}
      onBlur={() => {
        try {
          onChange(JSON.parse(text));
          setBad(false);
        } catch {
          setBad(true);
        }
      }}
      inputProps={{ 'aria-label': name, spellCheck: false }}
      InputProps={{ sx: { fontFamily: MONO, fontSize: 13 } }}
    />
  );
}

/** Where an effective value comes from (the resolver's source). Ops overrides are filled. */
export function SourceChip({ source }: { source: string | null | undefined }) {
  if (!source) return null;
  const hint = SOURCE_HINTS[source as SettingSource] ?? '';
  return (
    <Tooltip title={hint} describeChild>
      <Chip
        size="small"
        label={sourceLabel(source)}
        variant={source === 'ops' ? 'filled' : 'outlined'}
        color={source === 'ops' ? 'primary' : 'default'}
        sx={{ height: 20, fontSize: 11 }}
      />
    </Tooltip>
  );
}
